// src/components/Landing/MarketingShell.jsx
// Shared marketing-site chrome: design tokens, glassmorphic TopNav, and
// Footer. Drop <MarketingShell>{children}</MarketingShell> around any
// marketing page so the whole public surface stays visually consistent.
//
// PlaceholderLanding still defines its own copies because it predated this
// extraction and uses additional in-page nav (scroll-to-section). New
// marketing pages (Demo, Pricing, Privacy, Terms) use this shell.

import React, { useContext, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { Box, Flex, Text, Button, HStack, VStack, Container } from '@chakra-ui/react';

// ── Tokens ────────────────────────────────────────────────────────────
export const TOKENS = Object.freeze({
	BRAND: '#3D1E6D',
	BRAND_DEEP: '#241043',
	BRAND_LIGHT: '#5B3A8A',
	ACCENT: '#FB8601',
	ACCENT_DEEP: '#B14E00',
	INK: '#111113',
	MUTED: '#6B7280',
	CREAM: '#FBFBF7',
	LINE: 'rgba(17, 17, 19, 0.08)',
	HEAD_FONT: "'Poppins', sans-serif",
	BODY_FONT: "'DM Sans', sans-serif",
});

const { BRAND, ACCENT, ACCENT_DEEP, INK, MUTED, CREAM, LINE, HEAD_FONT, BODY_FONT } = TOKENS;

// ── Kicker (the small uppercase eyebrow used everywhere) ──────────────
export const Kicker = ({ children, color = ACCENT }) => (
	<Text
		as="span"
		fontSize={{ base: '12px', md: '13px' }}
		fontWeight="700"
		letterSpacing="0.12em"
		textTransform="uppercase"
		color={color}
		fontFamily={HEAD_FONT}
	>
		{children}
	</Text>
);

// ── TopNav (glassmorphic pill, scroll-aware) ──────────────────────────
export const MarketingTopNav = () => {
	const { isAuthenticated } = useContext(AuthContext);
	const [scrolled, setScrolled] = useState(false);
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 24);
		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });
		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	return (
		<Box
			position="fixed"
			top={{ base: 3, md: 5 }}
			left={0}
			right={0}
			zIndex={100}
			px={{ base: 3, md: 6 }}
		>
			<Flex
				mx="auto"
				maxW="1280px"
				h={{ base: '56px', md: '72px' }}
				bg={scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.7)'}
				backdropFilter="blur(16px)"
				borderRadius="full"
				px={{ base: 4, md: 6 }}
				align="center"
				justify="space-between"
				border={`1px solid ${LINE}`}
				transition="all 0.2s ease"
				boxShadow={scrolled ? '0 8px 24px rgba(17,17,19,0.06)' : 'none'}
			>
				<RouterLink to="/" aria-label="Eventory home">
					<Box as="img" src="/images/eventory-logo.svg" alt="Eventory" h={{ base: '22px', md: '28px' }} w="auto" />
				</RouterLink>

				<HStack gap={{ base: 5, md: 8 }} display={{ base: 'none', md: 'flex' }} fontFamily={BODY_FONT}>
					<Box as={RouterLink} to="/" fontSize="15px" fontWeight="600" color={INK} _hover={{ color: ACCENT_DEEP, textDecoration: 'none' }} textDecoration="none">
						Home
					</Box>
					<Box as={RouterLink} to="/demo" fontSize="15px" fontWeight="600" color={INK} _hover={{ color: ACCENT_DEEP, textDecoration: 'none' }} textDecoration="none">
						Demo
					</Box>
					<Box as={RouterLink} to="/pricing" fontSize="15px" fontWeight="600" color={INK} _hover={{ color: ACCENT_DEEP, textDecoration: 'none' }} textDecoration="none">
						Pricing
					</Box>
				</HStack>

				<Button
					as={RouterLink}
					to={isAuthenticated ? '/dashboard' : '/register'}
					bg={INK}
					color="white"
					borderRadius="full"
					px={{ base: 4, md: 6 }}
					h={{ base: '36px', md: '44px' }}
					fontSize={{ base: '13px', md: '15px' }}
					fontWeight="700"
					_hover={{ bg: BRAND, textDecoration: 'none' }}
					textDecoration="none"
				>
					{isAuthenticated ? 'My events →' : 'Get started'}
				</Button>
			</Flex>
		</Box>
	);
};

// ── Footer (matches PlaceholderLanding's footer pattern exactly) ──────
export const MarketingFooter = () => (
	<Box bg={CREAM} pt={{ base: 16, md: 20 }} pb={8} px={{ base: 6, md: 10, lg: 16 }}>
		<Container maxW="1400px" px={0}>
			<Flex direction={{ base: 'column', lg: 'row' }} justify="space-between" gap={{ base: 10, lg: 16 }} mb={{ base: 12, md: 16 }}>
				<VStack align="flex-start" spacing={5} maxW={{ base: '100%', lg: '420px' }}>
					<Box as="img" src="/images/eventory-logo.svg" alt="Eventory" h={{ base: '32px', md: '36px' }} />
					<Text fontSize={{ base: 'md', md: 'lg' }} color={MUTED} lineHeight="1.55">
						Run catering at Lagos events from one screen.
					</Text>
				</VStack>

				<Box
					display="grid"
					gridTemplateColumns={{ base: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
					gap={{ base: 8, md: 16 }}
					fontFamily={BODY_FONT}
				>
					<VStack align="flex-start" spacing={3}>
						<Text fontFamily={HEAD_FONT} fontWeight="700" fontSize="sm" color={INK} textTransform="uppercase" letterSpacing="0.08em" mb={1}>
							For event planners
						</Text>
						<RouterLink to="/" style={{ textDecoration: 'none' }}>
							<Text fontSize="md" color={MUTED} _hover={{ color: ACCENT_DEEP }}>What it does</Text>
						</RouterLink>
						<RouterLink to="/demo" style={{ textDecoration: 'none' }}>
							<Text fontSize="md" color={MUTED} _hover={{ color: ACCENT_DEEP }}>See the product</Text>
						</RouterLink>
						<RouterLink to="/pricing" style={{ textDecoration: 'none' }}>
							<Text fontSize="md" color={MUTED} _hover={{ color: ACCENT_DEEP }}>Pricing</Text>
						</RouterLink>
						<RouterLink to="/dashboard" style={{ textDecoration: 'none' }}>
							<Text fontSize="md" color={MUTED} _hover={{ color: ACCENT_DEEP }}>My events</Text>
						</RouterLink>
					</VStack>
					<VStack align="flex-start" spacing={3}>
						<Text fontFamily={HEAD_FONT} fontWeight="700" fontSize="sm" color={INK} textTransform="uppercase" letterSpacing="0.08em" mb={1}>
							Company
						</Text>
						<Text as="a" href="mailto:jason@geteventory.app" fontSize="md" color={MUTED} _hover={{ color: ACCENT_DEEP }} textDecoration="none">
							Contact us
						</Text>
					</VStack>
					<VStack align="flex-start" spacing={3}>
						<Text fontFamily={HEAD_FONT} fontWeight="700" fontSize="sm" color={INK} textTransform="uppercase" letterSpacing="0.08em" mb={1}>
							Legal
						</Text>
						<RouterLink to="/privacy" style={{ textDecoration: 'none' }}>
							<Text fontSize="md" color={MUTED} _hover={{ color: ACCENT_DEEP }}>Privacy</Text>
						</RouterLink>
						<RouterLink to="/terms" style={{ textDecoration: 'none' }}>
							<Text fontSize="md" color={MUTED} _hover={{ color: ACCENT_DEEP }}>Terms</Text>
						</RouterLink>
					</VStack>
				</Box>
			</Flex>

			<Box pt={6} borderTop={`1px solid ${LINE}`}>
				<Flex direction={{ base: 'column', sm: 'row' }} justify="space-between" align={{ base: 'flex-start', sm: 'center' }} gap={3}>
					<Text fontSize="xs" color={MUTED} letterSpacing="0.06em">
						© 2026 Eventory, a product of{' '}
						<Text as="a" href="https://www.stratospia.com" color={MUTED} textDecoration="underline" _hover={{ color: ACCENT_DEEP }}>
							Stratospia Limited
						</Text>{' '}
						(RC 9526323) · Lagos, Nigeria
					</Text>
					<Text fontSize="xs" color={MUTED} letterSpacing="0.06em">
						Made for the people who run the room.
					</Text>
				</Flex>
			</Box>
		</Container>
	</Box>
);

// ── Convenience wrapper ───────────────────────────────────────────────
export const MarketingShell = ({ children }) => (
	<Box w="100%" minH="100vh" bg="white" fontFamily={BODY_FONT} overflowX="hidden">
		<MarketingTopNav />
		<Box pt={{ base: 20, md: 28 }}>{children}</Box>
		<MarketingFooter />
	</Box>
);

export default MarketingShell;
